import { SITE_CONFIG } from "../../app/config";
import { useHead, buildComparisonSchema, buildFAQSchema } from "../../seo";
import { comparisonBySlug } from "./comparison-data";

type ComparePageProps = {
  slug: string;
};

export const ComparePage = ({ slug }: ComparePageProps): JSX.Element => {
  const comparison = comparisonBySlug.get(slug);

  if (!comparison) {
    return (
      <div className="shell">
        <div className="pseo-page">
          <header className="pseo-header">
            <a href="/compare" className="blog-back">← All Comparisons</a>
            <h1 className="pseo-title">Comparison Not Found</h1>
            <p className="pseo-subtitle">
              This comparison doesn't exist.{" "}
              <a href="/compare">Browse all comparisons →</a>
            </p>
          </header>
        </div>
      </div>
    );
  }

  const comparisonSchemas = buildComparisonSchema({
    title: comparison.metaTitle,
    description: comparison.metaDescription,
    path: `/compare/${comparison.slug}`,
    itemA: comparison.itemA.name,
    itemB: comparison.itemB.name,
  });

  const faqSchema = comparison.faq.length > 0 ? buildFAQSchema(comparison.faq) : null;
  const jsonLd = faqSchema ? [...comparisonSchemas, faqSchema] : comparisonSchemas;

  useHead({
    title: comparison.metaTitle,
    description: comparison.metaDescription,
    path: `/compare/${comparison.slug}`,
    ogType: "article",
    jsonLd,
  });

  return (
    <div className="shell">
      <div className="pseo-page">
        <header className="pseo-header">
          <a href="/compare" className="blog-back">← All Comparisons</a>
          <h1 className="pseo-title">{comparison.title}</h1>
          <p className="pseo-subtitle">{comparison.metaDescription}</p>
        </header>

        {/* Introduction */}
        <article className="compare-content">
          <div className="compare-intro">
            {comparison.introduction.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          {/* Side-by-side comparison */}
          <div className="compare-versus-grid">
            <div className="compare-versus-card compare-versus-a">
              <h2 className="compare-versus-name">{comparison.itemA.name}</h2>
              <div className="compare-pros">
                <h3>Pros</h3>
                <ul>
                  {comparison.itemA.pros.map((pro, i) => (
                    <li key={i}>
                      <span className="compare-icon-pro">+</span> {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="compare-cons">
                <h3>Cons</h3>
                <ul>
                  {comparison.itemA.cons.map((con, i) => (
                    <li key={i}>
                      <span className="compare-icon-con">−</span> {con}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="compare-best-for">
                <strong>Best for:</strong> {comparison.itemA.bestFor}
              </div>
            </div>

            <div className="compare-versus-divider">
              <span>VS</span>
            </div>

            <div className="compare-versus-card compare-versus-b">
              <h2 className="compare-versus-name">{comparison.itemB.name}</h2>
              <div className="compare-pros">
                <h3>Pros</h3>
                <ul>
                  {comparison.itemB.pros.map((pro, i) => (
                    <li key={i}>
                      <span className="compare-icon-pro">+</span> {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="compare-cons">
                <h3>Cons</h3>
                <ul>
                  {comparison.itemB.cons.map((con, i) => (
                    <li key={i}>
                      <span className="compare-icon-con">−</span> {con}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="compare-best-for">
                <strong>Best for:</strong> {comparison.itemB.bestFor}
              </div>
            </div>
          </div>

          {/* Detailed sections */}
          {comparison.sections.map((section, i) => (
            <section key={i} className="compare-section">
              <h2>{section.heading}</h2>
              {section.content.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </section>
          ))}

          {/* Verdict */}
          <section className="compare-verdict">
            <h2>The Verdict</h2>
            {comparison.verdict.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          {/* FAQ */}
          {comparison.faq.length > 0 && (
            <section className="compare-faq">
              <h2>Frequently Asked Questions</h2>
              {comparison.faq.map((item, i) => (
                <div key={i} className="compare-faq-item">
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </div>
              ))}
            </section>
          )}
        </article>

        <footer className="pseo-footer">
          <div className="blog-cta">
            <h3>Ready to Start Your Collection?</h3>
            <p>
              Paste your Spotify playlist and discover where to buy your favorite albums on vinyl
              and CD at the best prices.
            </p>
            <a href="/" className="blog-cta-button">
              Try {SITE_CONFIG.name} Free →
            </a>
          </div>
          <div className="pseo-nav-links">
            <a href="/compare">← All Comparisons</a>
            <a href="/glossary">Glossary</a>
            <a href="/blog">Blog</a>
            <a href="/">Home</a>
          </div>
        </footer>
      </div>
    </div>
  );
};
